import { useState, useMemo } from "react"
import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline"
import type { Adset, SelectedCreative } from "../types"

interface CustomAdset {
  id: string
  title: string
  creativeIds: string[]
}

interface CustomCreativeBuilderProps {
  selectedCreatives: SelectedCreative[]
  onComplete: (adsets: Adset[]) => void
  onBack: () => void
}

export default function CustomCreativeBuilder({
  selectedCreatives,
  onComplete,
  onBack,
}: CustomCreativeBuilderProps) {
  const [adsets, setAdsets] = useState<CustomAdset[]>([
    { id: crypto.randomUUID(), title: "", creativeIds: [] },
  ])

  const creativesById = useMemo(() => {
    const map = new Map<string, SelectedCreative>()
    for (const c of selectedCreatives) {
      map.set(c.id, c)
    }
    return map
  }, [selectedCreatives])

  const assignedIds = useMemo(() => {
    const set = new Set<string>()
    for (const adset of adsets) {
      for (const id of adset.creativeIds) {
        set.add(id)
      }
    }
    return set
  }, [adsets])

  const unassignedCreatives = useMemo(
    () => selectedCreatives.filter(c => !assignedIds.has(c.id)),
    [selectedCreatives, assignedIds],
  )

  const allAssigned =
    unassignedCreatives.length === 0 && selectedCreatives.length > 0

  const hasValidAdsets = adsets.some(
    a => a.title.trim() && a.creativeIds.length > 0,
  )

  const handleAddAdset = () => {
    setAdsets(prev => [
      ...prev,
      { id: crypto.randomUUID(), title: "", creativeIds: [] },
    ])
  }

  const handleRemoveAdset = (id: string) => {
    if (adsets.length <= 1) return
    setAdsets(prev => prev.filter(a => a.id !== id))
  }

  const handleTitleChange = (id: string, title: string) => {
    setAdsets(prev => prev.map(a => (a.id === id ? { ...a, title } : a)))
  }

  const handleAddCreative = (adsetId: string, creativeId: string) => {
    setAdsets(prev =>
      prev.map(a =>
        a.id === adsetId
          ? { ...a, creativeIds: [...a.creativeIds, creativeId] }
          : a,
      ),
    )
  }

  const handleRemoveCreative = (adsetId: string, creativeId: string) => {
    setAdsets(prev =>
      prev.map(a =>
        a.id === adsetId
          ? {
              ...a,
              creativeIds: a.creativeIds.filter(id => id !== creativeId),
            }
          : a,
      ),
    )
  }

  const handleComplete = () => {
    const result: Adset[] = adsets
      .filter(a => a.title.trim() && a.creativeIds.length > 0)
      .map(a => ({
        id: a.id,
        title: a.title.trim(),
        description: `${a.creativeIds.length} creative pieces`,
        creativeIds: a.creativeIds,
      }))
    onComplete(result)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Build Your Own Structure
        </h2>
        <p className="text-sm text-basic-light/60">
          Name your adsets and assign creative to each one. All{" "}
          {selectedCreatives.length} creative pieces must be assigned.
        </p>
      </div>

      {/* Unassigned creative pool */}
      {unassignedCreatives.length > 0 && (
        <div className="bg-system-gray-900/30 border border-system-gray-700/30 rounded-lg p-3">
          <p className="text-xs text-basic-light/50 mb-2">
            Unassigned ({unassignedCreatives.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {unassignedCreatives.map(c => (
              <div
                key={c.id}
                className="flex items-center gap-1.5 bg-system-gray-900/50 border border-system-gray-700/50 rounded px-2 py-1"
              >
                {c.thumbnail && (
                  <img
                    src={c.thumbnail}
                    alt={c.title}
                    className="w-6 h-6 rounded object-cover"
                  />
                )}
                <span
                  className="text-xs text-basic-light/70 max-w-[120px] truncate"
                  title={c.title}
                >
                  {c.title || c.id.slice(0, 8)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {allAssigned && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg px-3 py-2">
          <p className="text-xs text-primary">
            All creative has been assigned to adsets.
          </p>
        </div>
      )}

      {/* Adset list */}
      <div className="space-y-4">
        {adsets.map((adset, index) => (
          <div
            key={adset.id}
            className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4 space-y-3"
          >
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-basic-light/40 shrink-0">
                Adset {index + 1}
              </span>
              <input
                type="text"
                placeholder="Adset name..."
                value={adset.title}
                onChange={e => handleTitleChange(adset.id, e.target.value)}
                className="input input-bordered input-sm flex-1 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white placeholder:text-basic-light/30"
              />
              <button
                className="btn btn-ghost btn-sm btn-square text-error"
                onClick={() => handleRemoveAdset(adset.id)}
                disabled={adsets.length <= 1}
                title={
                  adsets.length <= 1
                    ? "At least one adset is required"
                    : "Remove adset"
                }
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Assigned creative thumbnails */}
            {adset.creativeIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {adset.creativeIds.map(cId => {
                  const creative = creativesById.get(cId)
                  return (
                    <div
                      key={cId}
                      className="flex items-center gap-1.5 bg-system-gray-700/30 rounded px-2 py-1 group"
                    >
                      {creative?.thumbnail && (
                        <img
                          src={creative.thumbnail}
                          alt={creative.title}
                          className="w-6 h-6 rounded object-cover"
                        />
                      )}
                      <span
                        className="text-xs text-basic-light/70 max-w-[100px] truncate"
                        title={creative?.title || cId}
                      >
                        {creative?.title || cId.slice(0, 8)}
                      </span>
                      <button
                        onClick={() => handleRemoveCreative(adset.id, cId)}
                        className="text-basic-light/40 hover:text-error transition-colors"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Add creative from unassigned pool */}
            {unassignedCreatives.length > 0 && (
              <div>
                <p className="text-[10px] text-basic-light/40 mb-1">
                  Click to add:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {unassignedCreatives.slice(0, 20).map(c => (
                    <button
                      key={c.id}
                      className="flex items-center gap-1 bg-system-gray-900/80 border border-system-gray-700/30 rounded px-1.5 py-0.5 hover:border-primary transition-colors"
                      onClick={() => handleAddCreative(adset.id, c.id)}
                    >
                      {c.thumbnail && (
                        <img
                          src={c.thumbnail}
                          alt={c.title}
                          className="w-5 h-5 rounded object-cover"
                        />
                      )}
                      <span className="text-[10px] text-basic-light/60 max-w-[80px] truncate">
                        {c.title || c.id.slice(0, 8)}
                      </span>
                    </button>
                  ))}
                  {unassignedCreatives.length > 20 && (
                    <span className="text-[10px] text-basic-light/40 self-center">
                      +{unassignedCreatives.length - 20} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          className="w-full border-2 border-dashed border-system-gray-700/50 rounded-lg p-4 flex items-center justify-center gap-2 text-sm text-basic-light/50 hover:text-basic-light/80 hover:border-system-gray-700 transition-colors"
          onClick={handleAddAdset}
        >
          <PlusIcon className="w-4 h-4" />
          Add Another Adset
        </button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={!hasValidAdsets || !allAssigned}
          title={
            !allAssigned
              ? "All creative must be assigned to an adset"
              : undefined
          }
          onClick={handleComplete}
        >
          Continue to Review
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
